import React, { useState } from 'react';
import { ChevronDown, ChevronUp, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';

interface RulesBreakdownProps {
  rulesApplied: string[];
  factorsAnalysis: {
    hauteur: any;
    plancher: any;
    exposition: any;
    energie: any;
    mitoyennete: any;
    volets: any;
    ventilation: any;
    ballonECS: any;
    globalScore: number;
  };
}

export const RulesBreakdown: React.FC<RulesBreakdownProps> = ({
  rulesApplied = [],
  factorsAnalysis
}) => {
  const [expanded, setExpanded] = useState(false); // Collapsed by default
  
  // Vérification de sécurité pour factorsAnalysis
  const safeFactorsAnalysis = factorsAnalysis || {
    hauteur: { level: 'INDÉTERMINÉ', impact: 'Non analysé' },
    plancher: { level: 'INDÉTERMINÉ', impact: 'Non analysé' },
    exposition: { level: 'INDÉTERMINÉ', impact: 'Non analysé' },
    energie: { level: 'INDÉTERMINÉ', impact: 'Non analysé' },
    mitoyennete: { level: 'INDÉTERMINÉ', impact: 'Non analysé' },
    volets: { level: 'INDÉTERMINÉ', impact: 'Non analysé' },
    ventilation: { level: 'INDÉTERMINÉ', impact: 'Non analysé' },
    ballonECS: { level: 'INDÉTERMINÉ', impact: 'Non analysé' },
    globalScore: 0
  };
  
  // Get level icon
  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'EXCELLENT':
      case 'OPTIMAL':
      case 'BON':
        return <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />;
      case 'MOYEN':
        return <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0" />;
      case 'DÉFAVORABLE':
      case 'CRITIQUE':
        return <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-gray-500 flex-shrink-0" />;
    }
  };
  
  // Get level color
  const getLevelColor = (level: string) => {
    switch (level) {
      case 'EXCELLENT':
      case 'OPTIMAL':
      case 'BON':
        return 'bg-green-50 border-green-200';
      case 'MOYEN':
        return 'bg-yellow-50 border-yellow-200';
      case 'DÉFAVORABLE':
      case 'CRITIQUE':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };
  
  // Get level badge
  const getLevelBadge = (level: string) => {
    switch (level) {
      case 'EXCELLENT':
      case 'OPTIMAL':
      case 'BON':
        return <Badge variant="success">{level}</Badge>;
      case 'MOYEN':
        return <Badge variant="warning">{level}</Badge>;
      case 'DÉFAVORABLE':
      case 'CRITIQUE':
        return <Badge variant="danger">{level}</Badge>;
      default:
        return <Badge variant="default">{level}</Badge>;
    }
  };
  
  // Format factor name
  const formatFactorName = (factor: string) => {
    switch (factor) {
      case 'hauteur':
        return 'Hauteur sous plafond';
      case 'plancher':
        return 'Type de plancher';
      case 'exposition':
        return 'Exposition thermique';
      case 'energie':
        return 'Coefficient énergétique';
      case 'mitoyennete':
        return 'Mitoyenneté';
      case 'volets':
        return 'Protection solaire des menuiseries (volets, stores)';
      case 'ventilation':
        return 'Système de ventilation';
      case 'ballonECS':
        return 'Ballon eau chaude sanitaire';
      default:
        return factor;
    }
  };
  
  // Count technical factors (excluding globalScore)
  const factorsCount = Object.keys(safeFactorsAnalysis).filter(key => key !== 'globalScore').length;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Règles métier appliquées</CardTitle>
            <CardDescription>
              {factorsCount} facteurs techniques analysés selon {rulesApplied?.length || 0} règles métier
            </CardDescription>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <>
                <ChevronUp className="w-4 h-4 mr-1" />
                Réduire
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4 mr-1" />
                Développer
              </>
            )}
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        {/* Factors analysis */}
        {expanded && (
          <div className="space-y-4">
          <h3 className="font-semibold text-gray-900 border-b pb-2">
            Facteurs déterminants
          </h3>
          
          {Object.entries(safeFactorsAnalysis)
            .filter(([key]) => key !== 'globalScore')
            .map(([key, value]) => {
              if (!value || typeof value !== 'object' || !value.level) return null;
              
              return (
                <div
                  key={key}
                  className={cn(
                    'p-4 rounded-lg border',
                    getLevelColor(value?.level || 'INDÉTERMINÉ')
                  )}
                >
                  <div className="flex items-start">
                    <div className="mr-3 mt-0.5">
                      {getLevelIcon(value?.level || 'INDÉTERMINÉ')}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900">
                          {formatFactorName(key)}
                        </h4>
                        {getLevelBadge(value?.level || 'INDÉTERMINÉ')}
                      </div>
                      
                      <p className="text-sm text-gray-600">
                        {value?.impact || 'Impact non analysé'}
                      </p>
                      
                      {value?.message && (
                        <p className="text-sm text-gray-600 mt-1 italic">
                          {value.message}
                        </p>
                      )}
                      
                      {value?.factor && (
                        <div className="mt-2 text-sm">
                          <span className="font-medium text-gray-700">Facteur:</span>
                          <span className="ml-2 text-gray-600">
                            {value.factor.toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Rules applied */}
        {expanded && (
          <div className="mt-8 space-y-4">
            <h3 className="font-semibold text-gray-900 border-b pb-2">
              Règles appliquées
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(rulesApplied || []).map((rule, index) => (
                <div
                  key={index}
                  className="p-3 bg-gray-50 border border-gray-200 rounded-lg"
                >
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                    <span className="text-sm text-gray-700">{rule || 'Règle non spécifiée'}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Summary when collapsed - removed as it's now in the header */}
      </CardContent>
    </Card>
  );
};

export default RulesBreakdown;